import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Divider,
  Empty,
  Radio,
  Row,
  Space,
  Tree,
  Input,
  Tag,
} from 'antd';
import React, { useContext, useCallback, useMemo } from 'react';

import { EXCLUDED, INCLUDED } from '@app/constants';
import { useMemoizeObject, useWindowSize } from '@app/hooks';
import {
  CHF_CLIENTS_FILTER,
  CHF_TAG_IDS,
  CHF_VISITS_FILTER,
} from '@app/pages/Clients/ClientsList/constants/filterKeysAndIds';
import { ClientFiltersContext } from '@app/providers';

import {
  getClientLogicalGroupIndex,
  getFiltersArray,
  hideFilterBlock,
} from '@pages/Clients/ClientsList/utils';

import type { NClientsList } from '@pages/Clients/ClientsList/ClientsList.types';
import { initialFiltersConfig } from '@pages/Clients/ClientsList/data/initialFiltersConfig';

import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { RadioChangeEvent } from 'antd';
import type { DataNode } from 'antd/es/tree';
import type { ReactElement } from 'react';

const { Search } = Input;
const defaultTagIncludingStatuses = [true];
const tagsStorageKey = 'client-filters-tags-including';

export const ClientsTags = ({
  includedFiltersIndex,
  filterGroupsId,
}: {
  includedFiltersIndex: number;
  filterGroupsId: string;
}) => {
  const {
    filters,
    allTags,
    searchedTags,
    allTagsWithoutAdaptation,
    tagIncludingStatuses,
    getClientTagsIncluding,
    setTagIncludingStatuses,
    setSearchedTags,
    setServerFilters,
    setFilters,
  } = useContext(ClientFiltersContext);
  const { innerWidth } = useWindowSize();

  const memoizedFilters = useMemoizeObject(filters) || initialFiltersConfig;
  const memoizedTagIncludingStatuses = useMemoizeObject(tagIncludingStatuses);

  const onChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const searchQuery = event.target?.value.toLowerCase().trim() || '';
    const newSearchedTags = allTags.filter((tag: DataNode) => {
      const { title } = tag;

      const searchTitle =
        'props' in (title as ReactElement)
          ? (title as ReactElement)?.props?.title
          : 'Name' in tag
          ? tag.Name
          : '';

      const isSearchedIntoCategories = String(searchTitle)
        .trim()
        .toLowerCase()
        .includes(searchQuery);

      if (isSearchedIntoCategories) {
        return true;
      }
    });

    setSearchedTags(newSearchedTags);
  };

  const extractData = useCallback(
    (key: string) => {
      const id = Number(key.split('-')[1]);
      return allTagsWithoutAdaptation.find(({ Id }) => Id === id);
    },
    [allTagsWithoutAdaptation]
  );

  const handleClickBySaveButton = useCallback(() => {
    setServerFilters(filters);

    hideFilterBlock('.clients-tags-label', innerWidth, includedFiltersIndex);
  }, [setServerFilters, filters, innerWidth, includedFiltersIndex]);

  const localDisabling = useMemo(() => {
    const excludedFiltersIndex = includedFiltersIndex - 1;
    const dynamicIndex = getClientTagsIncluding(
      includedFiltersIndex,
      memoizedTagIncludingStatuses || []
    )
      ? includedFiltersIndex
      : excludedFiltersIndex;

    return memoizedFilters?.[CHF_CLIENTS_FILTER]?.[dynamicIndex]?.Values?.[0]?.[
      CHF_TAG_IDS
    ] === null
      ? true
      : false;
  }, [
    includedFiltersIndex,
    memoizedFilters,
    memoizedTagIncludingStatuses,
    getClientTagsIncluding,
  ]);

  const checkedKeys = useMemo(() => {
    // Идет поиск по VisitsFilter, т.к. там 1 объект фильтров равен 1 логической группе
    const logicalGroupObject = memoizedFilters[CHF_VISITS_FILTER].find(
      ({ FilterGroupsId }) => FilterGroupsId === filterGroupsId
    );

    if (!logicalGroupObject) return [];

    const handledTagIncludingStatuses = memoizedTagIncludingStatuses || [
      ...defaultTagIncludingStatuses,
    ];
    const logicalGroupObjectIndex =
      memoizedFilters[CHF_VISITS_FILTER].indexOf(logicalGroupObject);
    const isIncludingMode =
      handledTagIncludingStatuses[logicalGroupObjectIndex];
    const modeFiltersIndex = isIncludingMode
      ? includedFiltersIndex
      : includedFiltersIndex - 1;

    const checkedKeysAsNumbers =
      memoizedFilters?.[CHF_CLIENTS_FILTER]?.[modeFiltersIndex]?.Values?.[0]
        ?.TagIds;
    const checkedKeysAsStrings = checkedKeysAsNumbers?.map(
      (item) => `parent-${item}`
    );

    return checkedKeysAsStrings || [];
  }, [
    filterGroupsId,
    includedFiltersIndex,
    memoizedFilters,
    memoizedTagIncludingStatuses,
  ]);

  const getNewFilters = useCallback(
    (areClientTagsIncluded: boolean, handledValues: number[]) => {
      const excludedFiltersIndex = includedFiltersIndex - 1;

      const getTagIds = (handledValues: number[]): number[] | null =>
        handledValues.length > 0 ? handledValues : null;

      const result = {
        ...filters,
        [CHF_CLIENTS_FILTER]: getFiltersArray<
          NClientsList.Filters['ClientsFilter']
        >({
          ...filters[CHF_CLIENTS_FILTER],
          [excludedFiltersIndex]: {
            ...filters.ClientsFilter?.[excludedFiltersIndex],
            IsInclude: false,
            Values: [
              {
                ...filters.ClientsFilter?.[excludedFiltersIndex]?.Values?.[0],
                TagIds: !areClientTagsIncluded
                  ? getTagIds(handledValues)
                  : null,
              },
            ],
          },
          [includedFiltersIndex]: {
            ...filters.ClientsFilter?.[includedFiltersIndex],
            IsInclude: true,
            Values: [
              {
                ...filters.ClientsFilter?.[includedFiltersIndex]?.Values?.[0],
                TagIds: areClientTagsIncluded ? getTagIds(handledValues) : null,
              },
            ],
          },
        }),
      };

      setFilters(result);
    },
    [includedFiltersIndex, filters, setFilters]
  );

  const onTreeCheck = useCallback(
    (keys: React.Key[]): void => {
      const handledValues = keys.map((key) => {
        const stringValue = String(key);
        return Number(stringValue.split('-')[1]);
      });

      getNewFilters(
        getClientTagsIncluding(includedFiltersIndex, tagIncludingStatuses),
        handledValues
      );
    },
    [
      includedFiltersIndex,
      tagIncludingStatuses,
      getNewFilters,
      getClientTagsIncluding,
    ]
  );

  const handleIncludingTagsChange = useCallback(
    (event: RadioChangeEvent) => {
      const value = event.target?.value || null;
      const areIncludedTags = value === INCLUDED ? true : false;
      const tagIncludingStatusesCopy = tagIncludingStatuses;
      const excludedFiltersIndex = includedFiltersIndex - 1;
      const newTagsObjectIndex = areIncludedTags
        ? excludedFiltersIndex
        : includedFiltersIndex;

      // TODO: в рамках task/3444 расширить тип NClientsList.FilterUnitValueKey,
      // чтобы не использовать в конце "as number[]"
      const handledValues = (filters?.[CHF_CLIENTS_FILTER]?.[newTagsObjectIndex]
        ?.Values?.[0]?.[CHF_TAG_IDS] || []) as number[];

      tagIncludingStatusesCopy[
        getClientLogicalGroupIndex(includedFiltersIndex)
      ] = areIncludedTags;
      setTagIncludingStatuses(tagIncludingStatusesCopy);

      getNewFilters(value === INCLUDED, handledValues);

      sessionStorage.setItem(
        tagsStorageKey,
        JSON.stringify(tagIncludingStatuses)
      );
    },
    [
      includedFiltersIndex,
      tagIncludingStatuses,
      filters,
      setTagIncludingStatuses,
      getNewFilters,
    ]
  );

  return (
    <div onClick={(event) => event.stopPropagation()} className="tags-filter">
      <Space direction="vertical">
        <Radio.Group
          defaultValue={
            getClientTagsIncluding(includedFiltersIndex, tagIncludingStatuses)
              ? INCLUDED
              : EXCLUDED
          }
          value={
            getClientTagsIncluding(includedFiltersIndex, tagIncludingStatuses)
              ? INCLUDED
              : EXCLUDED
          }
          onChange={handleIncludingTagsChange}
        >
          <Space direction="vertical">
            <Radio
              value={INCLUDED}
              checked={getClientTagsIncluding(
                includedFiltersIndex,
                tagIncludingStatuses
              )}
            >
              Имеет теги
            </Radio>
            <Radio
              value={EXCLUDED}
              checked={
                !getClientTagsIncluding(
                  includedFiltersIndex,
                  tagIncludingStatuses
                )
              }
            >
              Не имеет тегов
            </Radio>
          </Space>
        </Radio.Group>

        <div>
          <Search
            placeholder="Поиск по тегам"
            type="text"
            onChange={onChange}
            className="search-field"
          />

          {searchedTags.length > 0 ? (
            <Tree
              className="tags-tree"
              checkable
              checkedKeys={checkedKeys}
              onCheck={(checked, { checkedNodes }) => {
                const keys = checkedNodes.map(({ key }) => key);

                onTreeCheck(keys);
              }}
              treeData={searchedTags.map(({ key, title, children }) => ({
                key,
                title: (
                  <Tag
                    color={extractData(String(key))?.Color}
                    icon={
                      <FontAwesomeIcon
                        icon={extractData(String(key))?.IconId as IconProp}
                        className="tags-icon"
                      />
                    }
                    className="flex ai-center"
                    bordered={false}
                  >
                    {title as never}
                  </Tag>
                ),
                children,
              }))}
            />
          ) : (
            <Empty />
          )}

          <Divider className="tags-divider" />
          <Row justify="end">
            <Button
              type="primary"
              size="small"
              disabled={localDisabling}
              onClick={handleClickBySaveButton}
            >
              Выбрать
            </Button>
          </Row>
        </div>
      </Space>
    </div>
  );
};
